import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import AppShell from "../components/AppShell";
import PageHero from "../components/PageHero";
import { api, formatError } from "../lib/api";
import { toast } from "sonner";
import {
  Sparkles, Package, PenTool, Facebook, MessageSquare,
  Image as ImageIcon, FlaskConical, Lock, ArrowLeft, Loader2, Clock,
} from "lucide-react";

// Placeholder page rendered for every AI Studio tool while the actual
// generative implementations are being built. When the AI Studio add-on
// is inactive it shows the subscribe CTA; when active it shows a "Coming
// soon" panel with a waitlist toast.
const TOOLS = {
  "packaging-designer":  { name: "Packaging Designer",  icon: Package,       tagline: "Draft packaging directions from your brand brief.", eta: "Weeks — currently in design" },
  "logo-designer":       { name: "Logo Designer",       icon: PenTool,       tagline: "Explore logo concepts iterated on your positioning notes.", eta: "Weeks — currently in design" },
  "meta-ads-manager":    { name: "Meta Ads Manager",    icon: Facebook,      tagline: "Plan Facebook & Instagram ad campaigns with AI copy + targeting.", eta: "Weeks — currently in scoping" },
  "reddit-ads-manager":  { name: "Reddit Ads Manager",  icon: MessageSquare, tagline: "Community-aware Reddit ads with subreddit + creative picks.", eta: "Weeks — currently in scoping" },
  "ads-designer":        { name: "Ads Designer",        icon: ImageIcon,     tagline: "Generate on-brand ad creatives across formats.", eta: "Weeks — currently in design" },
  "formulation-creator": { name: "Formulation Creator", icon: FlaskConical,  tagline: "Get first-pass ingredient decks + benchmark specs.", eta: "Weeks — currently in R&D" },
};

export default function AIToolPlaceholder() {
  const { tool } = useParams();
  const spec = TOOLS[tool] || {
    name: "AI Tool",
    icon: Sparkles,
    tagline: "This tool isn't available yet.",
    eta: "Coming soon",
  };
  const Icon = spec.icon;

  const [status, setStatus] = useState(null);
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    api.get("/ai-addon/status").then((r) => setStatus(r.data)).catch(() => setStatus({ active: false }));
  }, []);

  const subscribe = async () => {
    setSubscribing(true);
    try {
      const r = await api.post("/ai-addon/checkout", { origin_url: window.location.origin });
      if (r.data.checkout_url) window.location.href = r.data.checkout_url;
    } catch (e) { toast.error(formatError(e)); setSubscribing(false); }
  };

  const isActive = !!status?.active;
  const isLoading = status === null;

  return (
    <AppShell title={spec.name}>
      <div className="w-full">
        <Link to="/ai-center" data-testid="ai-tool-back"
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-4">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to AI Center
        </Link>

        <PageHero
          eyebrow="AI Studio · Add-on"
          icon={Icon}
          title={spec.name}
          subtitle={spec.tagline}
        />

        {isLoading ? (
          <div className="mt-8 surface p-6 flex items-center gap-3 text-muted-foreground text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Checking your access…
          </div>
        ) : !isActive ? (
          <div className="mt-8 surface p-6" data-testid="ai-tool-locked">
            <div className="flex items-center gap-2">
              <Lock className="w-4 h-4 text-primary" />
              <p className="font-display text-xl">Subscribe to the AI Studio add-on</p>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              This tool is included in the $25/mo AI Studio bundle. Subscribe once
              and get every content-creation tool the moment it ships.
            </p>
            <ul className="mt-3 text-xs text-muted-foreground grid gap-1">
              <li>· 100 AI chat generations per month</li>
              <li>· 50 image generations per month</li>
              <li>· Metered overage billed on the first of the next month</li>
              <li>· Cancel anytime — access remains until the current period closes</li>
            </ul>
            <button
              onClick={subscribe}
              disabled={subscribing}
              data-testid="ai-tool-subscribe"
              className="mt-5 btn-primary text-sm inline-flex items-center gap-1.5 disabled:opacity-60"
            >
              {subscribing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              Subscribe · $25/mo
            </button>
          </div>
        ) : (
          <div className="mt-8 surface p-6" data-testid="ai-tool-coming-soon">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-shell-accent" />
              <p className="font-display text-xl">Coming soon</p>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              Your AI Studio subscription is active. We're finishing this tool now —
              you'll see it here the moment it ships, and we'll email you a heads-up.
            </p>
            <p className="mt-3 text-xs text-muted-foreground">
              Estimated timeline: <span className="font-semibold text-foreground">{spec.eta}</span>
            </p>
            <div className="mt-5">
              <Link to="/ai-center" data-testid="ai-tool-explore-center"
                className="btn-amber text-sm inline-flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5" /> Explore other tools
              </Link>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}
